import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Fab,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from "@mui/icons-material";
import ComprobanteTypeForm from "./ComprobanteTypeForm";
import { useComprobanteTypes } from "../../hooks/accounting/useComprobanteTypes";
import { useAuthContext } from "../../context/AuthProvider";

const ComprobanteTypesDashboard = () => {
  const [formState, setFormState] = useState({ open: false, type: null });
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { user } = useAuthContext();
  const { comprobanteTypes, isLoading, error, createMutation, updateMutation, deleteMutation } = useComprobanteTypes();

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleOpenForm = (type = null) => {
    setFormState({ open: true, type });
  };

  const handleCloseForm = () => {
    setFormState({ open: false, type: null });
  };

  const handleSubmit = useCallback((data) => {
    if (!user) {
      showAlert("Debe iniciar sesión", "error");
      return;
    }

    const payload = { ...data }; 

    if (formState.type) {
      payload.updbyid = user.EmpID; 
      updateMutation.mutate(
        { id: formState.type.tipcomid, data: payload },
        {
          onSuccess: () => {
            showAlert("Tipo de comprobante actualizado correctamente");
            handleCloseForm();
          },
          onError: (error) => {
            console.error("Error al actualizar:", error);
            showAlert(error.message || "Error al actualizar", "error");
          },
        }
      );
    } else {
      payload.crebyid = user.EmpID;
      createMutation.mutate(payload, {
        onSuccess: () => {
          showAlert("Tipo de comprobante creado correctamente");
          handleCloseForm();
        },
        onError: (error) => {
          console.error("Error al crear:", error);
          showAlert(error.message || "Error al crear", "error");
        },
      });
    }
  }, [formState.type, createMutation, updateMutation, user]);

  const handleConfirmDelete = () => {
    deleteMutation.mutate(formState.type.tipcomid, {
      onSuccess: () => {
        showAlert("Tipo de comprobante eliminado");
        setConfirmDialog({ open: false, type: null });
      },
      onError: () => showAlert("Error al eliminar", "error"),
    });
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filtered = comprobanteTypes.filter((item) =>
    item.tipcomnom.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Tipos de Comprobante</Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">Error al cargar: {error.message}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell><strong>Abrev.</strong></TableCell>
                <TableCell><strong>Nombre</strong></TableCell>
                <TableCell><strong>Descripción</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((type) => (
                <TableRow key={type.tipcomid}>
                  <TableCell>{type.tipcomcod}</TableCell>
                  <TableCell>{type.tipcomabr}</TableCell>
                  <TableCell>{type.tipcomnom}</TableCell>
                  <TableCell>{type.tipcomdes || "—"}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenForm(type)}>
                        <EditIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setConfirmDialog({ open: true, type })}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* FAB crear */}
      <Fab color="primary" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={() => handleOpenForm()}>
        <AddIcon />
      </Fab>

      {/* Modal formulario */}
      {formState.open && (
        <ComprobanteTypeForm
          open={formState.open}
          handleClose={handleCloseForm}
          onSubmit={handleSubmit}
          defaultValues={formState.type || {}}
        />
      )}

      {/* Confirmación */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null })}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea eliminar el tipo de comprobante "{confirmDialog.type?.tipcomnom}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null })}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default ComprobanteTypesDashboard;
